import { getRegisterUrlClientSide } from '@/app/utils/urlHelper';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import { useAuth0User } from '@/modules/Context/useAuth0User';
import JobSearchFilters from '@/modules/JobSearchFilters';
import { JobSearchFiltersFormHandle } from '@/modules/JobSearchFilters/components/JobSearchFiltersForm/JobSearchFiltersForm';
import { DEFAULT_RADIUS } from '@/utils/jobSearchBoxOptions';
import cx from 'classnames';
import { PATH } from 'constants/path';
import dynamic from 'next/dynamic';
import Router, { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { IArticleSummary, IFrequentlyAskedQuestion } from 'types/cms';
import {
  IJobDetail,
  IJobSpeciality,
  JobSearchWebCriteria,
  PaginationResult,
  URLQueryParamDict,
} from 'types/jobs';
import { pushGtmEvent } from 'utils/gtm';
import {
  SimilarSearchJobSummary,
  buildJobsListingBreadcrumbLabel,
  convertJobSearchQueryToJobAlertQuery,
  derivePageHeaderContext,
  generateJobListTrackingDetails,
} from 'utils/jobListingHelper';
import {
  scrollToResultsWhenRendered,
  setScrollInstantOnFilterChangeRerender,
} from 'utils/jobSearchAutoScroll';
import { useAnalytics } from 'utils/useAnalytics';

import { useMediaQuery } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';

import JobListingJobCardList from 'modules/JobListingJobCardList';

import styles from './JobListing.module.scss';
import JobAlertsBanner from './components/JobAlertsBanner/JobAlertsBanner';
import MobileFilterBar from './components/MobileFilterBar/MobileFilterBar';
import NoResultsMessage from './components/NoResultsMessage/NoResultsMessage';
import PageHeader from './components/PageHeader/PageHeader';
import ResultsHeader from './components/ResultsHeader/ResultsHeader';
import SimilarJobsSection from './components/SimilarJobsSection/SimilarJobsSection';
import { doctorStoryData } from './staticData/doctorStories';
import { JobListingAlertBanner } from './utils/JobListingTypes';
import { TestimonialsForContext } from './utils/filterTestimonialsForContext';
import { testimonialToDoctorStory } from './utils/testimonialToDoctorStory';

// Code-split below-the-fold sections into separate chunks from the main bundle. These stay
// SSR'd (the default), so they don't shrink the JS parsed/executed before hydration - the win
// is a smaller main chunk plus parallel, independently-cached fetches for each section's code.
const DoctorStories = dynamic(() =>
  import('./components/DoctorStories/DoctorStories').then(mod => ({
    default: mod.DoctorStories,
  })),
);
const GuideSection = dynamic(() =>
  import('./components/GuideSection/GuideSection').then(mod => ({ default: mod.GuideSection })),
);
const ExploreSimilarSearches = dynamic(() =>
  import('./components/ExploreSimilarSearches/ExploreSimilarSearches').then(mod => ({
    default: mod.ExploreSimilarSearches,
  })),
);
const FaqSection = dynamic(() =>
  import('./components/FaqSection/FaqSection').then(mod => ({ default: mod.FaqSection })),
);
// Pagination is kept as a static, SSR'd import (rather than `dynamic(..., { ssr: false })`) so
// paginated pages remain reachable without JS and the `.pagination` wrapper doesn't pop in
// post-hydration and shift the sections below it.
const JobAlertPreferencesDialog = dynamic(
  () =>
    import(
      '@/modules/JobAlerts/components/JobAlertPreferencesDialog/JobAlertPreferencesDialog'
    ).then(mod => ({ default: mod.JobAlertPreferencesDialog })),
  { ssr: false },
);

const SORT_GTM_VALUE_LABELS: Record<string, string> = {
  MostRecent: 'Most recent',
  HighestPaid: 'Salary',
};

const getJobListingBannerType = (query: JobSearchWebCriteria): JobListingAlertBanner => {
  const JOB_ALERT_CREATION_QUERY_KEYS = [
    'grade',
    'speciality',
    'rootSpeciality',
    'subspeciality',
    'workType',
    'startDate',
    'endDate',
    'keywords',
    'locationName',
    'location',
    'minPayRate',
    'minPayRateUnit',
  ];

  const isNonSchedulerLocationSelected = !!(query.lon && query.lat);
  const isPayRateRelatedSearch = !!(query.minPayRate && query.minPayRateUnit);
  const isMedicineOnlySearch = query.rootSpeciality === 'Medicine' && !query.subspeciality;
  const isAbleToCreateJobAlert = Boolean(JOB_ALERT_CREATION_QUERY_KEYS.find(key => query[key]));

  if (query.jobAlert === 'Manage') {
    return 'manage-job-alert';
  }

  if (isMedicineOnlySearch) {
    return 'select-subspeciality';
  }

  if (isAbleToCreateJobAlert) {
    if (isNonSchedulerLocationSelected || isPayRateRelatedSearch) {
      return 'none';
    } else {
      return 'job-alert-create';
    }
  } else {
    return 'select-search-criteria';
  }
};

interface JobListingProps {
  jobs: Omit<PaginationResult, 'page'>;
  similarJobs?: IJobDetail[];
  jobSpecialities: IJobSpeciality[];
  query: JobSearchWebCriteria;
  breadcrumbItems?: BreadcrumbItem[];
  origin?: string;
  guides?: IArticleSummary[];
  similarSearchJobs?: SimilarSearchJobSummary[];
  faqs?: IFrequentlyAskedQuestion[];
  // The testimonials callers have already ranked/capped against the search context (see
  // derivePageHeaderContext + filterTestimonialsForContext), bundled with whether any of them
  // actually matched that context - kept as a single object, rather than two separate props, so
  // the pair can't independently drift out of sync.
  testimonialsForContext?: TestimonialsForContext;
}

const JobListing = ({
  jobs: { total, data, limit },
  similarJobs = [],
  query,
  jobSpecialities,
  breadcrumbItems: serverBreadcrumbItems,
  origin,
  guides = [],
  similarSearchJobs = [],
  faqs = [],
  testimonialsForContext = { testimonials: [], hasContextMatch: false },
}: JobListingProps) => {
  const { testimonials, hasContextMatch: hasTestimonialContextMatch } = testimonialsForContext;
  const router = useRouter();
  const track = useAnalytics();
  const { user } = useAuth0User();

  const scrollToResultsRef = useRef<null | HTMLDivElement>(null);
  const filtersFormRef = useRef<JobSearchFiltersFormHandle>(null);

  const [showDialog, setShowDialog] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const trackingDetails = generateJobListTrackingDetails(query, user);

  const mobile = useMediaQuery(`(max-width: 600px)`);

  const radiusInKm = query.radius || DEFAULT_RADIUS;

  const hasSearchResults = !!data?.length;
  const hasSimilarResults = !!similarJobs?.length;
  const isNonSchedulerLocationSelected = !!(query.lon && query.lat);

  const [jobListingAlertBanner, setJobListingAlertBanner] = useState<JobListingAlertBanner>(
    getJobListingBannerType(query),
  );

  const jobSpecialitiesMapped = {};
  jobSpecialities.forEach(js => {
    jobSpecialitiesMapped[js.name] = js;
  });

  const {
    grade: pageHeaderGrade,
    speciality: pageHeaderSpeciality,
    location: pageHeaderLocation,
  } = derivePageHeaderContext(query);

  const mappedDoctorStories = testimonials.map(testimonialToDoctorStory);
  // Fall back to the hand-authored stories if the CMS has nothing yet, so a CMS outage or an
  // empty content set doesn't silently remove the "Placed, paid and supported" section site-wide.
  const doctorStories = mappedDoctorStories.length > 0 ? mappedDoctorStories : doctorStoryData;

  // Use server-rendered breadcrumbs if available, otherwise fallback to old logic
  const fallbackBreadcrumbs: BreadcrumbItem[] = [
    { label: 'Medrecruit', href: PATH.HOME },
    { label: 'Doctor Jobs', href: PATH.JOBSLIST },
  ];

  // Only add the third breadcrumb item if there are specific filters
  if (pageHeaderGrade || pageHeaderSpeciality || pageHeaderLocation) {
    fallbackBreadcrumbs.push({
      label: buildJobsListingBreadcrumbLabel({
        speciality: pageHeaderSpeciality,
        location: pageHeaderLocation,
      }),
    });
  }

  const pageHeaderBreadcrumbItems = serverBreadcrumbItems || fallbackBreadcrumbs;
  const pageHeaderState = query.state && query.state !== 'none' ? query.state : undefined;
  const pageHeaderWorkType = query.workType || undefined;

  const sortByClick = type => {
    let search: URLQueryParamDict = {};
    search = Object.assign(search, query, { sortBy: type });
    if (!type) {
      delete search.sortBy;
    }
    router.push({ pathname: PATH.JOBSLIST, query: search });
  };

  const handleSortByChange = (type: string) => {
    setScrollInstantOnFilterChangeRerender();
    sortByClick(type);
    track('Job results Sort By', { sort_by: type, ...trackingDetails });
    if (type) {
      pushGtmEvent({
        event: 'results_sort',
        element: 'Sort',
        sort_value: SORT_GTM_VALUE_LABELS[type] ?? type,
      });
    }
  };

  const handleJobAlertBannerSaveCLick = () => {
    track('Set up job alert button click', trackingDetails);
    if (!user) {
      // noinspection JSIgnoredPromiseFromCall

      Router.push(getRegisterUrlClientSide('create_job_alert'));
    } else {
      setShowDialog(true);
    }
  };

  const handleJobAlertManageButtonClick = () => {
    track('Manage job alerts button click', trackingDetails);
    router.push(PATH.JOBS_ALERTS);
  };

  const onJobAlertSave = () => {
    setJobListingAlertBanner('manage-job-alert');
  };

  useEffect(() => {
    setJobListingAlertBanner(getJobListingBannerType(query));
  }, [router.query]);

  useEffect(() => {
    scrollToResultsWhenRendered(scrollToResultsRef);
  }, [router.query]);

  useEffect(() => {
    track('Job Search Results', {
      ...query,
      count: total,
      regCTAJobResultsView: user === null,
    });
  }, [router.query]);

  return (
    <div data-testid="job-listing-module">
      <PageHeader
        breadcrumbItems={pageHeaderBreadcrumbItems}
        grade={pageHeaderGrade}
        speciality={pageHeaderSpeciality}
        location={pageHeaderLocation}
        total={total}
        state={pageHeaderState}
      />

      <div className={styles.container}>
        <div>
          <div className={styles.alertContainer} ref={scrollToResultsRef}>
            <JobAlertsBanner
              onSave={handleJobAlertBannerSaveCLick}
              onManage={handleJobAlertManageButtonClick}
              jobListingAlertBanner={jobListingAlertBanner}
            />
          </div>

          {!hasSearchResults && <NoResultsMessage />}

          {hasSearchResults && (
            <>
              <ResultsHeader
                jobSpecialitiesMapped={jobSpecialitiesMapped}
                query={query}
                total={total}
                isNonSchedulerLocationSelected={isNonSchedulerLocationSelected}
                mobile={mobile}
                radiusInKm={radiusInKm}
                onSortChange={handleSortByChange}
              />

              <div className={styles.jobCard} data-testid="job-search-jobs-listing-results">
                <JobListingJobCardList
                  jobs={data}
                  isUserLoggedIn={!!user}
                  query={query}
                  suppressCTABanner={hasSimilarResults}
                  includeJsonLd
                  origin={origin}
                />
              </div>
            </>
          )}

          {hasSimilarResults && (
            <SimilarJobsSection
              className={cx({ [styles.noResultsSimilarJobs]: !hasSearchResults })}
              // we should always have grade in this case
              grade={query.grade ?? ''}
              speciality={query.speciality}
              rootSpeciality={query.rootSpeciality}
              subspeciality={query.subspeciality}
              hasSearchResults={hasSearchResults}
              jobs={similarJobs}
              isUserLoggedIn={!!user}
              query={query}
              includeJsonLd
              origin={origin}
            />
          )}

          {hasSearchResults && total > limit && (
            <div className={styles.pagination}>
              <Pagination
                count={Math.ceil(total / limit)}
                page={(query.page && +query.page) || 1}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={(_e, value) => {
                  track('JobsList pagination', { 'Page number': value });
                  pushGtmEvent({
                    event: 'pagination_click',
                    element: 'Pagination',
                    page_number: value,
                  });

                  if (query['listing']) {
                    delete query['listing'];
                  }

                  let newQuery: URLQueryParamDict = {};
                  newQuery = Object.assign(newQuery, query);
                  newQuery.page = value;
                  router.push({ pathname: location.pathname, query: newQuery });
                }}
              />
            </div>
          )}
        </div>

        <div className={styles.filters}>
          <JobSearchFilters ref={filtersFormRef} onOpenChange={setIsFiltersOpen} query={query} />
        </div>

        <MobileFilterBar
          isFiltersOpen={isFiltersOpen}
          onFiltersToggle={() => filtersFormRef.current?.toggle()}
          sortBy={query.sortBy ?? ''}
          onSortChange={handleSortByChange}
        />
      </div>

      <DoctorStories
        doctorStories={doctorStories}
        // Only claim the section is specific to the current grade/speciality/location when at
        // least one shown testimonial is actually tied to that context - otherwise every card is
        // unmatched filler (or the static fallback), and the claim would be misleading.
        grade={hasTestimonialContextMatch ? pageHeaderGrade : undefined}
        speciality={hasTestimonialContextMatch ? pageHeaderSpeciality : undefined}
        location={hasTestimonialContextMatch ? pageHeaderLocation : undefined}
      />

      <GuideSection guides={guides} />

      <ExploreSimilarSearches
        jobs={similarSearchJobs}
        origin={origin ?? ''}
        grade={pageHeaderGrade}
        speciality={pageHeaderSpeciality}
        state={pageHeaderLocation}
        workType={pageHeaderWorkType}
      />

      <FaqSection
        faqs={faqs}
        grade={pageHeaderGrade}
        speciality={pageHeaderSpeciality}
        location={pageHeaderLocation}
      />

      <JobAlertPreferencesDialog
        query={convertJobSearchQueryToJobAlertQuery(query)}
        show={showDialog}
        hide={() => {
          setShowDialog(false);
        }}
        successCallBack={onJobAlertSave}
      />
    </div>
  );
};

export default JobListing;
